import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = "quanentry_admin_token";

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => useContext(AdminAuthContext);

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: `${BACKEND_URL}/api`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    // STEP 2 Section 6: API Trace Interceptor
    instance.interceptors.request.use((config) => {
      config.metadata = { startTime: new Date() };
      console.debug(`[API TRACE REQUEST] ${config.method?.toUpperCase()} ${config.url}`, {
        baseURL: config.baseURL,
        timestamp: new Date().toISOString()
      });
      return config;
    });

    instance.interceptors.response.use(
      (response) => {
        const duration = new Date() - (response.config.metadata?.startTime || new Date());
        const payloadLength = response.data ? JSON.stringify(response.data).length : 0;
        console.debug(`[API TRACE RESPONSE] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
          status: response.status,
          durationMs: duration,
          payloadLengthBytes: payloadLength,
          timestamp: new Date().toISOString()
        });
        return response;
      },
      (error) => {
        const duration = error.config?.metadata?.startTime ? new Date() - error.config.metadata.startTime : 0;
        console.debug(`[API TRACE ERROR] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
          status: error.response?.status || 'NETWORK_ERROR',
          durationMs: duration,
          errorMessage: error.message,
          timestamp: new Date().toISOString()
        });
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token]);

  const login = useCallback(async (email, password, otp_code) => {
    console.debug("[API LOGIN ATTEMPT]", { email, timestamp: new Date().toISOString() });
    const res = await axios.post(`${BACKEND_URL}/api/admin/login`, {
      email,
      password,
      otp_code: otp_code || null,
    });
    if (res.data.requires_mfa) {
      return { requires_mfa: true };
    }
    const newToken = res.data.access_token;
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    return { requires_mfa: false };
  }, []);

  const logout = useCallback(() => {
    console.debug("[LOGOUT EXECUTED]", { timestamp: new Date().toISOString() });
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAdmin(null);
  }, []);

  // Validate token by trying to fetch stats
  useEffect(() => {
    let cancelled = false;
    const validate = async () => {
      if (!token) {
        setAdmin(null);
        setLoading(false);
        return;
      }
      try {
        await axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAdmin({ token });
      } catch (e) {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setAdmin(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AdminAuthContext.Provider value={{ token, admin, loading, login, logout, api }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

